import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ONBOARDING_TOTAL_STEPS,
  onboardingProfileSchema,
  onboardingRoles,
  onboardingUses,
  projectSlugSchema,
  WELCOME_GRANT_CREDITS,
  WELCOME_GRANT_EXPIRY_DAYS,
  type Invitation,
  type MeResponse,
  type OnboardingProfile,
  type OnboardingState,
  type ProjectSummary,
} from "@zworks/console-contracts";
import { Zap } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router";
import { TenantMark } from "@/components/shell/project-switcher";
import { PageHeader } from "@/components/bespoke";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ApiError,
  createProject,
  currentUserQuery,
  inviteMember,
  projectsQuery,
  saveOnboarding,
} from "@/lib/api";
import { formatCredits, formatDate } from "@/lib/format";

const roleLabels: Record<OnboardingProfile["role"], string> = {
  "software-engineer": "Software engineer",
  "engineering-manager": "Engineering manager",
  qa: "QA",
  product: "Product",
  other: "Other",
};

const useLabels: Record<OnboardingProfile["uses"][number], { title: string; detail: string }> = {
  "ship-features": {
    title: "Ship features and fixes with agents",
    detail: "Plan, build, test, and open pull requests.",
  },
  "review-quality": {
    title: "Review and quality checks",
    detail: "Code review, test reports, and evidence.",
  },
  migrations: {
    title: "Migrations and upgrades",
    detail: "Larger, step-by-step changes with approvals.",
  },
};

const inviteRoleLabels: Record<Invitation["role"], string> = {
  owner: "Owner",
  developer: "Developer",
  approver: "Approver",
  viewer: "Viewer",
};

/** "Beacon Telemetry" becomes "beacon-telemetry". The API rechecks it, so this only previews. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

type Failure = { message: string; requestId?: string };

function failureOf(error: unknown): Failure {
  const apiError =
    error instanceof ApiError ? error : new ApiError("The request could not be completed.", 500);
  return { message: apiError.problem?.detail ?? apiError.message, requestId: apiError.requestId };
}

function FailureAlert({ failure }: { failure: Failure | null }) {
  if (!failure) return null;
  return (
    <div role="alert" className="flex flex-col gap-0.5 text-[0.8125rem]">
      <p className="font-medium text-danger">{failure.message}</p>
      {failure.requestId ? (
        <p className="text-muted-foreground">
          Reference <span className="font-mono text-foreground">{failure.requestId}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The frame every onboarding step lives in, as the boards draw it: the sign-out link, the
 * progress dots with the "Step N of 4" label, and one centred column. Status is always a
 * labelled badge, never colour alone.
 */
function OnboardingFrame({ step, children }: { step: number; children: React.ReactNode }) {
  async function onSignOut() {
    await fetch("/api/v1/auth/logout", { method: "POST", credentials: "same-origin" });
    window.location.href = "/login";
  }
  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b px-4">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <span
            aria-hidden
            className="grid size-6 place-items-center rounded-md bg-primary text-xs font-semibold text-primary-foreground"
          >
            Z
          </span>
          Zworks
        </span>
        <span className="ml-auto" />
        <button
          type="button"
          onClick={() => void onSignOut()}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Sign out
        </button>
      </header>
      <main className="flex flex-1 items-start justify-center px-4 py-10">
        <div className="flex w-full flex-col gap-6" style={{ width: "min(28rem, 100%)" }}>
          <div className="flex flex-col gap-3">
            <div
              className="flex items-center gap-1.5"
              role="img"
              aria-label={`Step ${step} of ${ONBOARDING_TOTAL_STEPS}`}
            >
              {Array.from({ length: ONBOARDING_TOTAL_STEPS }, (_, index) => (
                <span
                  key={index}
                  aria-hidden
                  className={
                    index < step
                      ? "h-1.5 w-8 rounded-full bg-primary"
                      : "h-1.5 w-8 rounded-full bg-muted"
                  }
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">
              Step {step} of {ONBOARDING_TOTAL_STEPS}
            </span>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * Step 1, as the Onboarding1 board draws it: the name prefilled from the provider, the role
 * select, the intended-use checkboxes, and the terms agreement. Continue stays disabled until
 * every answer is valid, and saving also renames the caller server-side.
 */
function AboutYouStep({
  me,
  onSaved,
}: {
  me: MeResponse;
  onSaved: (state: OnboardingState) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(me.onboarding.profile?.displayName ?? me.displayName);
  const [role, setRole] = React.useState<OnboardingProfile["role"] | "">(
    me.onboarding.profile?.role ?? "",
  );
  const [uses, setUses] = React.useState<OnboardingProfile["uses"]>(
    me.onboarding.profile ? [...me.onboarding.profile.uses] : [],
  );
  const [agreed, setAgreed] = React.useState(me.onboarding.profile?.agreedToTerms ?? false);
  const [saving, setSaving] = React.useState(false);
  const [failure, setFailure] = React.useState<Failure | null>(null);

  function toggleUse(use: OnboardingProfile["uses"][number]) {
    setUses((current) =>
      current.includes(use) ? current.filter((item) => item !== use) : [...current, use],
    );
  }

  const parsed = onboardingProfileSchema.safeParse({
    displayName: name.trim(),
    role: role === "" ? undefined : role,
    uses,
    agreedToTerms: agreed,
  });
  const valid = parsed.success;

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setFailure(null);
    try {
      const saved = await saveOnboarding({ currentStep: 2, profile: parsed.data });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      onSaved(saved);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader title="Welcome to Zworks" />
      <form aria-label="About you" className="flex flex-col gap-5" onSubmit={onSubmit}>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="onboarding-name">
            Full name
          </label>
          <Input
            id="onboarding-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
          />
          <p className="text-[0.8125rem] text-muted-foreground">
            Prefilled from your identity provider. Teammates see this name.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="onboarding-role">
            Your role
          </label>
          <Select value={role} onValueChange={(value) => setRole(value as typeof role)}>
            <SelectTrigger id="onboarding-role">
              <SelectValue placeholder="Choose your role" />
            </SelectTrigger>
            <SelectContent>
              {onboardingRoles.map((option) => (
                <SelectItem key={option} value={option}>
                  {roleLabels[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">What will you use Zworks for?</legend>
          {onboardingUses.map((use) => (
            <label
              key={use}
              className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:border-foreground/45 has-checked:border-primary has-checked:bg-selected"
            >
              <input
                type="checkbox"
                className="mt-1 size-4 shrink-0 accent-primary"
                checked={uses.includes(use)}
                onChange={() => toggleUse(use)}
                aria-label={useLabels[use].title}
              />
              <span>
                <span className="block text-sm font-medium">{useLabels[use].title}</span>
                <span className="block text-[0.8125rem] text-muted-foreground">
                  {useLabels[use].detail}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="flex cursor-pointer items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-primary"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            aria-label="I agree to the Terms of use and the Privacy policy"
          />
          <span>
            I agree to the{" "}
            <a className="underline" href="#">
              Terms of use
            </a>{" "}
            and the{" "}
            <a className="underline" href="#">
              Privacy policy
            </a>
            .
          </span>
        </label>
        <FailureAlert failure={failure} />
        <Button type="submit" size="lg" className="w-full" disabled={!valid || saving}>
          {saving ? "Saving…" : "Continue"}
        </Button>
      </form>
    </>
  );
}

/**
 * Step 2, as the Onboarding2 board draws it: the name, the address with its preview, the grant
 * panel, and the owner statement. It reuses the project creation endpoint, so the caller becomes
 * the owner and the starter grant arrives with the project. A taken address is shown inline.
 */
function FirstProjectStep({
  onSaved,
}: {
  onSaved: (state: OnboardingState, project: ProjectSummary) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [description, setDescription] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [failure, setFailure] = React.useState<Failure | null>(null);

  const slugIssues = projectSlugSchema.safeParse(slug);
  const slugError =
    slug.length > 0 && !slugIssues.success
      ? (slugIssues.error.issues.at(-1)?.message ?? "Invalid project address.")
      : null;
  const valid = name.trim().length > 0 && slugIssues.success;

  function onNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(suggestSlug(value));
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || creating) return;
    setCreating(true);
    setFailure(null);
    try {
      const project = await createProject({
        name: name.trim(),
        slug,
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      const saved = await saveOnboarding({ currentStep: 3, projectId: project.id });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      onSaved(saved, project);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <PageHeader title="Create your first project" />
      <form
        aria-label="Create your first project"
        className="flex flex-col gap-4"
        onSubmit={onSubmit}
      >
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="onboarding-project-name">
            Project name
          </label>
          <Input
            id="onboarding-project-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="onboarding-project-slug">
            Project address
          </label>
          <div className="flex items-center overflow-hidden rounded-md border border-input">
            <span className="flex h-9 items-center border-r border-input bg-muted px-3 text-sm text-muted-foreground">
              zworks.example/p/
            </span>
            <Input
              id="onboarding-project-slug"
              value={slug}
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(event.target.value);
              }}
              autoComplete="off"
              className="rounded-none border-0 shadow-none"
            />
          </div>
          {slugError ? (
            <p role="alert" className="text-[0.8125rem] text-danger">
              {slugError}
            </p>
          ) : (
            <p className="text-[0.8125rem] text-muted-foreground">
              Letters, numbers, and hyphens. It cannot be changed later.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="onboarding-project-description">
            Description (optional)
          </label>
          <Input
            id="onboarding-project-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="flex gap-3 rounded-lg border p-3">
          <Zap className="mt-0.5 size-4 shrink-0 text-accent-text" aria-hidden />
          <div>
            <p className="text-sm font-medium">
              {formatCredits(WELCOME_GRANT_CREDITS)} free credits, added when you create it
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              About 10 typical runs. They expire {WELCOME_GRANT_EXPIRY_DAYS} days after they are
              granted. You can top up whenever you like.
            </p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          You will be the project owner. You can invite members, manage billing and credentials, and
          transfer ownership later.
        </p>
        <FailureAlert failure={failure} />
        <div className="flex items-center gap-2">
          <Button type="submit" size="lg" className="flex-1" disabled={!valid || creating}>
            {creating ? "Creating…" : "Create project"}
          </Button>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          Were you invited to a project instead?{" "}
          <a className="underline" href="/projects">
            Check my invitations
          </a>
        </p>
      </form>
    </>
  );
}

/**
 * Step 3, as the Onboarding3 board draws it: optional invites by email with a role, reusing the
 * members invitation endpoint. Members share the project's credits; only the owner buys more.
 * Skipping leaves the step-2 project usable and moves to the summary.
 */
function InviteTeamStep({
  me,
  onSaved,
  onSkipped,
}: {
  me: MeResponse;
  onSaved: (state: OnboardingState) => void;
  onSkipped: (state: OnboardingState) => void;
}) {
  const projectId = me.onboarding.projectId;
  const queryClient = useQueryClient();
  const [rows, setRows] = React.useState<{ email: string; role: Invitation["role"] }[]>([
    { email: "", role: "developer" },
  ]);
  const [invited, setInvited] = React.useState<Invitation[]>([]);
  const [sending, setSending] = React.useState(false);
  const [finishing, setFinishing] = React.useState(false);
  const [failure, setFailure] = React.useState<Failure | null>(null);

  const pending = rows.filter((row) => row.email.trim().length > 0);
  const sendLabel =
    pending.length === 0
      ? "Send invitations"
      : pending.length === 1
        ? "Send 1 invitation"
        : `Send ${pending.length} invitations`;

  function setRow(index: number, patch: Partial<{ email: string; role: Invitation["role"] }>) {
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows((current) => [...current, { email: "", role: "developer" as const }]);
  }

  async function finish(state: OnboardingState) {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    onSaved(state);
  }

  async function onSend() {
    if (!projectId || sending || pending.length === 0) return;
    setSending(true);
    setFailure(null);
    try {
      const sent: Invitation[] = [];
      for (const row of pending) {
        const result = await inviteMember(projectId, { email: row.email.trim(), role: row.role });
        sent.push(result.invitation);
      }
      setInvited((current) => [...current, ...sent]);
      setRows([{ email: "", role: "developer" }]);
      await saveOnboarding({ currentStep: 3, projectId });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setSending(false);
    }
  }

  async function onContinue() {
    if (finishing || !projectId) return;
    setFinishing(true);
    setFailure(null);
    try {
      const saved = await saveOnboarding({ currentStep: 4, projectId });
      await finish(saved);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setFinishing(false);
    }
  }

  async function onSkip() {
    if (finishing) return;
    setFinishing(true);
    setFailure(null);
    try {
      const saved = await saveOnboarding({
        currentStep: 4,
        ...(projectId ? { projectId } : {}),
        completed: false,
      }).catch(() => null);
      const next =
        saved ??
        ({
          currentStep: 4,
          completedAt: null,
          profile: me.onboarding.profile,
          projectId,
        } satisfies OnboardingState);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      onSkipped(next);
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setFinishing(false);
    }
  }

  return (
    <>
      <PageHeader title="Invite your team" />
      <div className="flex flex-col gap-3" aria-label="Invite your team">
        {rows.map((row, index) => (
          <div key={index} className="flex gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor={`onboarding-invite-email-${index}`}>
                {index === 0 ? "Email addresses" : `Email address ${index + 1}`}
              </label>
              <Input
                id={`onboarding-invite-email-${index}`}
                type="email"
                autoComplete="email"
                value={row.email}
                onChange={(event) => setRow(index, { email: event.target.value })}
                placeholder="name@company.example"
              />
            </div>
            <div className="flex w-32 flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor={`onboarding-invite-role-${index}`}>
                {index === 0 ? "Role" : `Role ${index + 1}`}
              </label>
              <Select
                value={row.role}
                onValueChange={(value) => setRow(index, { role: value as Invitation["role"] })}
              >
                <SelectTrigger id={`onboarding-invite-role-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["developer", "approver", "viewer"] as const).map((option) => (
                    <SelectItem key={option} value={option}>
                      {inviteRoleLabels[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            Add another
          </Button>
        </div>
        {invited.length > 0 ? (
          <ul className="flex flex-col gap-2" aria-label="Invitations sent">
            {invited.map((invitation) => (
              <li
                key={invitation.id}
                className="flex items-center gap-3 rounded-lg border p-3 text-sm"
              >
                <span
                  aria-hidden
                  className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground"
                >
                  {invitation.email.slice(0, 2).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate">{invitation.email}</span>
                <Badge variant="secondary">{inviteRoleLabels[invitation.role]}</Badge>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Members use the project&apos;s credits. Everyone you invite shares the{" "}
          {formatCredits(WELCOME_GRANT_CREDITS)} credits. Only you, as owner, can buy more or manage
          billing.
        </p>
        <FailureAlert failure={failure} />
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={sending || pending.length === 0}
            onClick={() => void onSend()}
          >
            {sending ? "Sending…" : sendLabel}
          </Button>
          {invited.length > 0 ? (
            <Button
              type="button"
              size="lg"
              variant="secondary"
              className="w-full"
              disabled={finishing}
              onClick={() => void onContinue()}
            >
              {finishing ? "Continuing…" : "Continue"}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={sending || finishing}
            onClick={() => void onSkip()}
          >
            {finishing ? "Skipping…" : "Skip for now"}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * Step 4, as the Onboarding4 board draws it: the project tile, the grant with its expiry, the
 * invitations sent, and three next steps. Finishing records completion and lands on the project.
 */
function AllSetStep({
  me,
  project,
  invitedCount,
  onDone,
}: {
  me: MeResponse;
  project: ProjectSummary | null;
  invitedCount: number;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [finishing, setFinishing] = React.useState(false);
  const [failure, setFailure] = React.useState<Failure | null>(null);
  const firstName = me.displayName.split(/\s+/)[0] ?? me.displayName;

  async function onFinish() {
    if (finishing) return;
    setFinishing(true);
    setFailure(null);
    try {
      await saveOnboarding({
        currentStep: 4,
        ...(me.onboarding.projectId ? { projectId: me.onboarding.projectId } : {}),
        completed: true,
      });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      onDone();
      navigate(project ? `/projects` : "/");
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setFinishing(false);
    }
  }

  return (
    <>
      <PageHeader title={`You are all set, ${firstName}`} />
      <div className="flex flex-col gap-3">
        {project ? (
          <Card role="article" aria-label={project.name}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex items-center gap-3">
                <TenantMark name={project.name} id={project.id} className="size-10 text-sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{project.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    zworks.example/p/{project.slug}
                  </p>
                </div>
                <Badge variant="outline">Owner</Badge>
              </div>
              <div className="flex items-center justify-between border-t pt-3 text-sm">
                <span className="text-muted-foreground">
                  {formatCredits(project.creditBalance ?? WELCOME_GRANT_CREDITS)} credits available
                </span>
                <Badge variant="success">Granted</Badge>
              </div>
              {project.welcomeGrantExpiresAt ? (
                <p className="text-[0.8125rem] text-muted-foreground">
                  Welcome grant · expires {formatDate(project.welcomeGrantExpiresAt)}
                </p>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <EmptyState
            title="No project yet"
            description="Your project will appear here once it is created."
          />
        )}
        {invitedCount > 0 ? (
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="info">
              {invitedCount} {invitedCount === 1 ? "invitation" : "invitations"} sent
            </Badge>
            <span className="text-muted-foreground">They join when they accept.</span>
            <Badge variant="secondary">Pending</Badge>
          </div>
        ) : null}
        <div className="rounded-lg border p-4">
          <p className="text-sm font-medium">Good next steps</p>
          <ul className="mt-2 flex flex-col gap-2 text-sm" aria-label="Good next steps">
            <li className="flex flex-col gap-0.5">
              <span className="font-medium">Connect a repository</span>
              <span className="text-muted-foreground">
                Give runs something to read and a place to open pull requests.
              </span>
            </li>
            <li className="flex flex-col gap-0.5">
              <span className="font-medium">Set your budget</span>
              <span className="text-muted-foreground">
                Check the monthly limit and the per-run cap before agents spend.
              </span>
            </li>
            <li className="flex flex-col gap-0.5">
              <span className="font-medium">Run your first task</span>
              <span className="text-muted-foreground">
                Give an agent a piece of work. Runs tracks these steps.
              </span>
            </li>
          </ul>
        </div>
        <FailureAlert failure={failure} />
        <Button
          type="button"
          size="lg"
          className="w-full"
          disabled={finishing}
          onClick={() => void onFinish()}
        >
          {finishing ? "Finishing…" : project ? `Go to ${project.name}` : "Go to your projects"}
        </Button>
        <p className="text-center text-[0.8125rem] text-muted-foreground">
          Stuck at any point? Zot is in the bottom corner of every page.
        </p>
      </div>
    </>
  );
}

/**
 * The four-step first-login flow: about you, first project, invite your team, all set. A reload
 * resumes at the saved step; once completed or skipped, the flow never shows again and the app
 * shell owns the route. Newcomers land on step 1; returning users never arrive here.
 */
export function OnboardingPage() {
  const me = useQuery(currentUserQuery);
  const projects = useQuery({
    ...projectsQuery,
    enabled: (me.data?.onboarding.currentStep ?? 0) >= 4,
  });
  const [localStep, setLocalStep] = React.useState<number | null>(null);
  const [project, setProject] = React.useState<ProjectSummary | null>(null);
  const [invitedCount, setInvitedCount] = React.useState(0);

  const savedStep = me.data?.onboarding.currentStep ?? null;
  const step = localStep ?? savedStep;
  const projectId = me.data?.onboarding.projectId ?? null;
  const summaries = projects.data ?? [];
  const summary =
    project ?? summaries.find((item) => item.id === projectId) ?? summaries[0] ?? null;

  if (me.isPending) {
    return (
      <OnboardingFrame step={1}>
        <LoadingState label="Loading your account" rows={3} />
      </OnboardingFrame>
    );
  }

  if (me.isError) {
    const failure = failureOf(me.error);
    return (
      <OnboardingFrame step={1}>
        <ErrorState
          title="Your account could not be loaded"
          description={failure.message}
          requestId={failure.requestId ?? "unknown"}
          onRetry={() => void me.refetch()}
        />
      </OnboardingFrame>
    );
  }

  const person = me.data;
  if (person.onboarding.currentStep === null && localStep === null) {
    return (
      <OnboardingFrame step={ONBOARDING_TOTAL_STEPS}>
        <EmptyState
          title="You are all set"
          description="Onboarding is complete. Your projects are waiting."
          action={{ label: "Go to your projects", onClick: () => (window.location.href = "/") }}
        />
      </OnboardingFrame>
    );
  }

  return (
    <OnboardingFrame step={step ?? 1}>
      {(step ?? 1) === 1 ? (
        <AboutYouStep me={person} onSaved={(state) => setLocalStep(state.currentStep ?? 2)} />
      ) : (step ?? 1) === 2 ? (
        <FirstProjectStep
          onSaved={(state, created) => {
            setProject(created);
            setLocalStep(state.currentStep ?? 3);
          }}
        />
      ) : (step ?? 1) === 3 ? (
        <InviteTeamStep
          me={person}
          onSaved={(state) => {
            setInvitedCount((count) => count + 1);
            setLocalStep(state.currentStep ?? 4);
          }}
          onSkipped={(state) => setLocalStep(state.currentStep ?? 4)}
        />
      ) : (
        <AllSetStep
          me={person}
          project={summary}
          invitedCount={invitedCount}
          onDone={() => setLocalStep(null)}
        />
      )}
    </OnboardingFrame>
  );
}
